import React, { useCallback, useContext, useMemo } from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { css } from 'emotion';

import { DisplayGridColumnPropType } from '../../../prop-types/display-grid-column';

import { useVirtualizedRows } from './hooks';
import { Context } from '../context';
import { RowContext } from '../row-context';
import { DisplayGridBodyRow } from './row';

import style from './index.module.less';
import columnStyle from '../display-grid-column-group.module.css';

export const sanitize = (str) =>
  str
    .toLowerCase()
    .replace(/[^\s\w-]+/g, '')
    .replace(/[\s-]+/g, '-');

export const DisplayGridBody = React.memo(({ columns, pinned = false }) => {
  const { onRowDoubleClick: onRowDblClick, renderCheckbox } = useContext(Context);

  const { onExpandedRowsChanged, setExpandedGroupRows, totalSelectedGroups, setTotalSelectedGroups } = useContext(RowContext);

  const onRowDoubleClick = useCallback(
    (event, rowData) => {
      if (event.stopPropagation) {
        event.stopPropagation();
      }

      if (onRowDblClick) {
        onRowDblClick(rowData);
      }
    },
    [onRowDblClick],
  );

  // When a row is toggled, we only care about the currently-expanded row id's,
  // so delete anything that's collapsed, and return the updated object.
  const onToggleRow = useCallback(
    (id, isGroupByRow) => {
      if (isGroupByRow) {
        setExpandedGroupRows((expandedGroupRows) => {
          const newSet = new Set(expandedGroupRows);
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return newSet;
        });
      } else if (onExpandedRowsChanged) {
        onExpandedRowsChanged((expanded) => {
          const newExpanded = new Set(expanded);

          if (newExpanded.has(id)) {
            newExpanded.delete(id);
          } else {
            newExpanded.add(id);
          }

          return newExpanded;
        });
      }
    },
    [onExpandedRowsChanged, setExpandedGroupRows],
  );

  /**
   * Get our virtualRows collection here, which will be up to one full
   * page of records (based on display height), plus up to one set of
   * size BUFFER_ROWS (defined in ../constants.js) of rows above and
   * below the current scroll position of the grid. This is to keep the
   * DOM lightweight and help with performance issues related to
   * react-beautiful-dnd, which does css updates in the head of the
   * document and causes recalculation of every node in the document.
   */
  const { bottomOffset, rows: virtualRows, topOffset } = useVirtualizedRows(pinned);

  // Use the offset info obtained from the useVirtualizedRows hook to
  // calculate the margin bottom and top on the virtual rendered window.
  const offsetClassName = useMemo(
    () => css`
      margin-bottom: ${bottomOffset}px;
      margin-top: ${topOffset}px;
    `,
    [bottomOffset, topOffset],
  );
  const className = classnames(style['grid-rows-container'], offsetClassName);

  return (
    <div
      className={classnames({
        [columnStyle['pinned-columns']]: pinned,
        [columnStyle['non-pinned-columns']]: !pinned,
      })}
    >
      <div className={className}>
        {virtualRows.map((datum, index) => {
          if (datum.isGroupbySeparator) {
            return <div key={`groupBySeparator${index}`} className={style['group-by-grid-separator']} />;
          }
          return (
            <DisplayGridBodyRow
              {...datum.metadata}
              key={datum.metadata.key}
              columns={columns}
              data={datum.data}
              onRowDoubleClick={onRowDoubleClick}
              onToggleRow={onToggleRow}
              pinned={pinned}
              renderCheckbox={renderCheckbox}
              setTotalSelectedGroups={setTotalSelectedGroups}
              totalSelectedGroups={totalSelectedGroups}
            />
          );
        })}
      </div>
    </div>
  );
});

DisplayGridBody.propTypes = {
  columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
  pinned: PropTypes.bool,
};
